import { transformFromFormValues, transformDayjsToStrings, transformToFormValues } from "../formServiceFunctions";
import _ from "lodash";
import { getProductData } from "../Product/ProductService/productService";
import { apiPatch } from "../Api/apiCall";
import { UseForm } from "../../types/UseForm";
import { Organization } from "../../types/Organization";
import { Appointment } from "../../types/Appointment";
import { Person } from "../../types/Person";
import { Customer } from "../../types/Customer";
import { Address } from "../../types/Address";
import { Court } from "../../types/Court";
import { CaseInsurance } from "../../types/CaseInsurance";
import { CasePerson } from "../../types/CasePerson";
import { ProductUnion } from "../../types/ProductUnion";
import { OrganizationEmail } from "../../types/OrganizationEmail";

export const persist = async (values: UseForm["values"], origProduct: ProductUnion) => {
  const transformedDateValues = transformDayjsToStrings(
    values[origProduct.productClassName],
    origProduct.productClassName
  );

  let updateData = difference(transformedDateValues, transformToFormValues(origProduct));
  updateData = transformFromFormValues(origProduct.productClassName, updateData);

  const productPersister = getProductData(origProduct.productClassName, "persist");
  await productPersister(updateData, origProduct);
};

export const persistAddress = async (id: number, addressData: Partial<Address>) => {
  return await apiPatch("addresses", id, addressData);
};

export const persistCaseInsurance = async (id: number, caseInsuranceUpdateData: Partial<CaseInsurance>) => {
  return await apiPatch("case_insurances", id, caseInsuranceUpdateData);
};

export const persistCustomer = async (id: number, customerData: Partial<Customer>) => {
  return await apiPatch("customers", id, customerData);
};

export const persistPerson = async (id: number, personData: Partial<Person>) => {
  return await apiPatch("people", id, personData);
};

export const persistCasePerson = async (id: number, casePersonData: Partial<CasePerson>) => {
  return await apiPatch("case_people", id, casePersonData);
};

export const persistCourt = async (id: number, courtData: Partial<Court>) => {
  return await apiPatch("courts", id, courtData);
};

export const persistAppointment = async (id: number, appointmentData: Partial<Appointment>) => {
  return await apiPatch("appointments", id, appointmentData);
};

export const persistBankData = async (id: number, bankData: Partial<Organization>) => {
  return await apiPatch("bank_datas", id, bankData);
};

export const getUpdateData = (
  object: UseForm["values"],
  product: UseForm["values"] | null = null,
  basePath: string = "",
  forceBackofficeCaseId = false
) => {
  let resultSet = _.pickBy(object, (value) => {
    return !_.isPlainObject(value);
  });

  if (!_.isEmpty(resultSet) || forceBackofficeCaseId) {
    if (product !== null) {
      resultSet.backofficeCaseId = product.backofficeCase.id;
    }
    resultSet.basePath = basePath;
  }

  resultSet = _.mapValues(resultSet, (value) => {
    return value !== "" ? value : null;
  });

  return resultSet;
};

export const difference = (object: Record<string, any>, base: Record<string, any>) => {
  function changes(object: Record<string, any>, base: Record<string, any>) {
    return _.transform(object, function (result: Record<string, any>, value, key) {
      if (!_.isEqual(value, base[key])) {
        result[key] = _.isPlainObject(value) && _.isPlainObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }

  return changes(object, base);
};

export const trimValueIfSet = (field: string | number, values: UseForm["values"]) => {
  if (values[field]) {
    values[field] = _.trim(values[field]);
  }
  return values;
};

export const sanitizePersonData = (personData: Partial<Person>) => {
  let sanitizedPersonData = _.cloneDeep(personData);
  sanitizedPersonData = trimValueIfSet("givenName", sanitizedPersonData);
  sanitizedPersonData = trimValueIfSet("familyName", sanitizedPersonData);
  return sanitizedPersonData;
};

export const sanitizeAddressData = (addressData: Partial<Address>) => {
  let sanitizedAddressData = _.cloneDeep(addressData);
  sanitizedAddressData = trimValueIfSet("streetAddress", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postalCode", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("addressLocality", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postOfficeBoxAddressLocality", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postOfficeBoxPostalCode", sanitizedAddressData);
  return sanitizedAddressData;
};

export const sanitizeOrganizationData = (organizationData: Partial<Organization>) => {
  let sanitizedOrganizationData = _.cloneDeep(organizationData);
  sanitizedOrganizationData = trimValueIfSet("name", sanitizedOrganizationData);
  sanitizedOrganizationData = trimValueIfSet("legalName", sanitizedOrganizationData);
  sanitizedOrganizationData = trimValueIfSet("telephone", sanitizedOrganizationData);
  return sanitizedOrganizationData;
};

export const sanitizeOrganizationEmailData = (organizationEmailData: Partial<OrganizationEmail>) => {
  let sanitizedOrganizationEmailData = _.cloneDeep(organizationEmailData);
  sanitizedOrganizationEmailData = trimValueIfSet("email", sanitizedOrganizationEmailData);
  return sanitizedOrganizationEmailData;
};
